import { Alert, AlertTitle, Stack, Typography } from "@mui/material";
import {
  PtPatientOut,
  useIguanaApiV1GetCoursesQuery,
} from "@providers/hop-ord-server/api";
import { dateOfBirthFormat, toLocaleDateTimeString } from "@utils";
import { Controller, UseFormReturn } from "react-hook-form";
import { SiteOption } from "../types";
import ListBox from "./ListBox";
import ListItem from "./ListItem";

interface Props {
  patient: PtPatientOut;
  tenantId: number;
  clinic: string;
  form: UseFormReturn<{
    clinic: SiteOption | null;
    searchPatient: string;
    selectedCourses: {
      [key: string]: boolean;
    };
  }>;
}

const SelectPrescriptionContent = ({
  patient,
  tenantId,
  clinic,
  form,
}: Props) => {
  const {
    data: courses,
    isFetching: isFetchingCourses,
    isError: isCoursesError,
  } = useIguanaApiV1GetCoursesQuery(
    {
      mrn: patient.patientId,
      tenantId: tenantId,
      treatingDepartment: clinic,
    },
    { refetchOnMountOrArgChange: true },
  );

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Stack sx={{ alignItems: "start" }}>
        <Typography variant="subtitle2">
          {patient.firstName} {patient.lastName}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
          }}
        >
          Patient ID: {patient.patientId} | Date of Birth:{" "}
          {patient.dateOfBirth
            ? toLocaleDateTimeString(patient.dateOfBirth, "date")
            : "-"}
        </Typography>
      </Stack>
      {isCoursesError ? (
        <Alert
          severity="error"
          sx={{ backgroundColor: "error.light", color: "error.dark" }}
        >
          <AlertTitle>Connection Error</AlertTitle>
          Please try again or create a Careplan manually
        </Alert>
      ) : (
        <Stack sx={{ maxHeight: 180 }}>
          <ListBox
            title="Courses"
            noResultsMessage="No results found"
            isLoading={isFetchingCourses}
            noResults={!!courses && courses.length === 0}
          >
            {courses &&
              courses.map((course) => {
                const lastModifiedDate = course.lastModifiedDate
                  ? dateOfBirthFormat(course.lastModifiedDate)
                  : "-";
                return (
                  <Controller
                    key={course.courseId}
                    control={form.control}
                    name={`selectedCourses.${course.courseId}`}
                    defaultValue={false}
                    render={({ field }) => (
                      <ListItem
                        key={course.courseId}
                        title={course.courseId}
                        titleRight={`Last modified: ${lastModifiedDate}`}
                        subtitle={course.sites
                          .map((site) => site.prescriptionName)
                          .join(", ")}
                        checked={field.value ?? false}
                        variant="checkbox"
                        onChange={field.onChange}
                      />
                    )}
                  />
                );
              })}
          </ListBox>
        </Stack>
      )}
    </Stack>
  );
};

export default SelectPrescriptionContent;
